import {
  VolumeOffIcon,
  Volume2Icon,
  Trash2,
  LogOut,
  KeyRound,
} from "lucide-react";
import { Button } from "@primitives/button";
import { useTranslation } from "react-i18next";
import { muteChat, rotateCSK, unmuteChat } from "@/middleware/chats";
import { useToast } from "@/hooks/useToast";
import { useNavigationState } from "@/zustand/NavigationState";
import { MemberRole } from "@/server/types/member";
import { ChatType } from "@/server/types/chat";
import { CHAT_HUB_ID } from "@/config/constants";

import AddMembers from "./add-members/AddMembers";
import EditChatDetails from "./EditChatDetails";

import { useState } from "react";
import DeleteP2PChat from "@/components/shared/dialogs/DeleteP2PChat";
import DeleteGroupChat from "@/components/shared/dialogs/DeleteGroupChat";
import LeaveGroupChat from "@/components/shared/dialogs/LeaveGroupChat";

export default function ChatDetailsActions() {
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatMuted = useNavigationState((state) => state.activeChatMuted);
  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeChatHubId = useNavigationState((state) => state.activeChatHubId);
  const [openDialog, setOpenDialog] = useState<
    "delete-p2p-chat" | "delete-group-chat" | "leave-group-chat" | null
  >(null);
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleMuteChat = async () => {
    if (!activeChatId) {
      return;
    }
    try {
      await muteChat(activeChatId);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleUnMuteChat = async () => {
    if (!activeChatId) {
      return;
    }
    try {
      await unmuteChat(activeChatId);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const [rotating, setRotating] = useState(false);
  const handleRotateKey = async () => {
    if (!activeChatId || rotating) {
      return;
    }
    setRotating(true);
    try {
      await rotateCSK(activeChatId, "manual_debug");
      toast({ duration: 1500, description: "CSK rotated" });
    } catch (error) {
      toast({
        duration: 2000,
        description:
          error instanceof Error ? error.message : "CSK rotation failed",
        variant: "destructive",
      });
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="flex justify-center gap-3">
      {(activeChatRoleAsMember === MemberRole.OWNER ||
        activeChatRoleAsMember === MemberRole.ADMIN ||
        activeChatRoleAsMember === MemberRole.MODERATOR) &&
        activeChatType === ChatType.GROUP &&
        // Architect chats: only the creator (Owner) may add humans. After the
        // creator deletes their account, the OA bot owns the chat and no
        // surviving human's role changes — an Admin/Moderator would otherwise
        // see an add-members control the backend will refuse ("hide rather
        // than 403", the BE's one ask from the bot-succession decision).
        (activeChatHubId !== CHAT_HUB_ID ||
          activeChatRoleAsMember === MemberRole.OWNER) && <AddMembers />}

      {activeChatMuted ? (
        <Button
          onClick={handleUnMuteChat}
          size="icon"
          variant="ghost"
          className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-foreground hover:bg-transparent"
        >
          <VolumeOffIcon className="size-4" />
          <span className="text-sm font-normal">{t("unmute")}</span>
        </Button>
      ) : (
        <Button
          onClick={handleMuteChat}
          size="icon"
          variant="ghost"
          className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-foreground hover:bg-transparent"
        >
          <Volume2Icon className="size-4" />
          <span className="text-sm font-normal">{t("mute")}</span>
        </Button>
      )}

      {activeChatType === ChatType.P2P && (
        <Button
          onClick={() => setOpenDialog("delete-p2p-chat")}
          size="icon"
          variant="ghost"
          className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-destructive hover:bg-transparent"
        >
          <Trash2 className="size-4" />
          <span className="text-sm font-normal">{t("delete")}</span>
        </Button>
      )}

      {import.meta.env.DEV && (
        <Button
          onClick={handleRotateKey}
          disabled={rotating}
          size="icon"
          variant="ghost"
          className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-foreground hover:bg-transparent"
        >
          <KeyRound className={`size-4 ${rotating ? "animate-spin" : ""}`} />
          <span className="text-sm font-normal">Rotate</span>
        </Button>
      )}

      {activeChatType === ChatType.GROUP &&
        (activeChatRoleAsMember === MemberRole.OWNER ? (
          <>
            <EditChatDetails />

            <Button
              onClick={() => setOpenDialog("delete-group-chat")}
              size="icon"
              variant="ghost"
              className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-destructive hover:bg-transparent"
            >
              <Trash2 className="size-4" />
              <span className="text-sm font-normal">{t("delete")}</span>
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setOpenDialog("leave-group-chat")}
            size="icon"
            variant="ghost"
            className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-destructive hover:bg-transparent"
          >
            <LogOut className="size-4" />
            <span className="text-sm font-normal">{t("leave")}</span>
          </Button>
        ))}

      {/* CONFIRMATION DIALOGS */}

      <DeleteP2PChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-p2p-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-p2p-chat" : null);
        }}
      />

      <DeleteGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />

      <LeaveGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "leave-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />
    </div>
  );
}
